const APP_ROLES = new Set(["super_admin", "org_admin", "staff"]);

export type AccessTokenClaims = {
  sub: string;
  email: string | null;
  name: string | null;
  tenantId: string | null;
  roles: string[];
};

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "="));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function extractAppRoles(payload: Record<string, unknown>): string[] {
  const collected: string[] = [];

  const topLevel = payload.roles;
  if (Array.isArray(topLevel)) {
    collected.push(...topLevel.filter((r): r is string => typeof r === "string"));
  } else if (typeof topLevel === "string") {
    collected.push(topLevel);
  }

  const realmAccess = payload.realm_access;
  if (realmAccess && typeof realmAccess === "object" && "roles" in realmAccess) {
    const realmRoles = (realmAccess as { roles?: unknown }).roles;
    if (Array.isArray(realmRoles)) {
      collected.push(...realmRoles.filter((r): r is string => typeof r === "string"));
    }
  }

  const seen = new Set<string>();
  const unique: string[] = [];
  for (const role of collected) {
    if (!seen.has(role)) {
      seen.add(role);
      unique.push(role);
    }
  }
  return unique.filter((role) => APP_ROLES.has(role));
}

export function claimsFromAccessToken(accessToken: string): AccessTokenClaims | null {
  const payload = decodeJwtPayload(accessToken);
  if (!payload || typeof payload.sub !== "string") return null;

  const tenantId = payload.tenant_id;
  return {
    sub: payload.sub,
    email: typeof payload.email === "string" ? payload.email : null,
    name: typeof payload.name === "string" ? payload.name : null,
    tenantId: typeof tenantId === "string" ? tenantId : null,
    roles: extractAppRoles(payload),
  };
}

export function accessTokenIsSuperAdmin(accessToken: string | undefined): boolean {
  if (!accessToken) return false;
  const claims = claimsFromAccessToken(accessToken);
  return claims?.roles.includes("super_admin") ?? false;
}
